import React, { useMemo } from 'react';
import {
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { formatCurrency } from '../utils/format';
import type { CumulativeChartMonth } from '../api/trackerApi';
import type { TrackerValues } from '../models/TrackerValues';
import type { MonthData } from '../types/tracking';

interface Props {
  values: TrackerValues;
  months: MonthData[];
  backendChartData?: Record<string, CumulativeChartMonth> | null;
}

// Constants - 12 tax year months (April 6 - May 5, May 6 - June 5, etc.)
const TAX_YEAR_MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'] as const;

/**
 * Get tracking chart colors from CSS variables defined in @theme
 * Uses distinct chart colors (gold, pink, blue) for better differentiation
 */
function getTrackingColors(): { income: string; contributions: string; tax: string } {
  if (typeof window === 'undefined') {
    // Fallback for SSR
    return {
      income: '#b58840',  // gold (chart-6)
      contributions: '#d53880',  // pink (chart-3)
      tax: '#2563eb',  // blue (chart-1)
    };
  }

  const style = getComputedStyle(document.documentElement);
  return {
    income: style.getPropertyValue('--color-chart-6').trim(),  // gold
    contributions: style.getPropertyValue('--color-chart-3').trim(),  // pink
    tax: style.getPropertyValue('--color-chart-1').trim(),  // blue
  };
}

const CHART_CONFIG = {
  margin: { top: 5, right: 30, left: 20, bottom: 5 },
  height: 350,
  strokeWidth: {
    actual: 2,
    projected: 2,
    target: 1
  },
  strokeDasharray: {
    projected: '5 5',
    target: '3 3'
  }
} as const;

/**
 * Get axis styles from CSS variables defined in @theme
 * Uses gray colors for axis ticks and lines
 * @returns Object with tick and line style properties for recharts axes
 */
function getAxisStyles(): { tick: { fontSize: number; fill: string }; line: { stroke: string } } {
  if (typeof window === 'undefined') {
    return {
      tick: { fontSize: 12, fill: '#6b7280' },
      line: { stroke: '#e5e7eb' }
    };
  }

  const style = getComputedStyle(document.documentElement);
  return {
    tick: { fontSize: 12, fill: style.getPropertyValue('--color-gray-500').trim() },
    line: { stroke: style.getPropertyValue('--color-gray-200').trim() }
  };
}

const AXIS_TICK_STYLE = getAxisStyles().tick;
const AXIS_LINE_STYLE = getAxisStyles().line;

type ChartDataPoint = {
  month: string;
  actualPostSacrificeIncome: number | null;
  actualContributions: number | null;
  actualTax: number | null;
  expectedIncomeManual: number;
  targetContributions: number;
  // For bar chart - behind target
  incomeBehind: number | null;
  contributionsBehind: number | null;
  taxBehind: number | null;
  // For bar chart - ahead of target
  incomeAhead: number | null;
  contributionsAhead: number | null;
  taxAhead: number | null;
  // Base values for stacking when ahead
  incomeBase: number | null;
  contributionsBase: number | null;
  taxBase: number | null;
}

type CustomTooltipProps = {
  active?: boolean;
  payload?: Array<{
    value: number | null;
    name: string;
    color: string;
    dataKey?: string;
  }>;
  label?: string;
}

const formatYAxisTick = (value: number): string => `£${(value / 1000).toFixed(0)}k`;

const formatTooltipValue = (value: number | null): string => {
  if (value === null) return '-';
  return formatCurrency(value).replace('.00', '');
};

// Chart title
const CHART_TITLE = 'Cumulative Progress';

const CustomTooltip = React.memo(function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload) return null;

  return (
    <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
      <p className="font-semibold text-gray-900 mb-2">{label}</p>
      {payload.map((entry, index) => {
        if (entry.value === null) return null;
        return (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-600">{entry.name}:</span>
            <span className="font-medium text-gray-900">
              {formatTooltipValue(entry.value as number)}
            </span>
          </div>
        );
      })}
    </div>
  );
});

export const CumulativeChart: React.FC<Props> = ({ values, months, backendChartData }) => {
  // Get colors inside component to ensure CSS variables are loaded
  const CHART_COLORS = useMemo(() => getTrackingColors(), []);

  const legendFormatter = React.useCallback((value: string) => (
    <span className="text-sm text-gray-700">{value}</span>
  ), []);

  const chartData = useMemo<ChartDataPoint[]>(() => {
    // Use backend chart data if available (for logged-in users)
    if (backendChartData) {
      return TAX_YEAR_MONTHS.map((monthName) => {
        const backendMonth = backendChartData[monthName];
        if (!backendMonth) {
          return {
            month: monthName,
            actualPostSacrificeIncome: null,
            actualContributions: null,
            actualTax: null,
            expectedIncomeManual: 0,
            targetContributions: 0,
            incomeBehind: null,
            contributionsBehind: null,
            taxBehind: null,
            incomeAhead: null,
            contributionsAhead: null,
            taxAhead: null,
            incomeBase: null,
            contributionsBase: null,
            taxBase: null,
          };
        }

        const actualIncome = backendMonth.cumulative_income ? parseFloat(backendMonth.cumulative_income) : null;
        const actualContributions = backendMonth.cumulative_contributions ? parseFloat(backendMonth.cumulative_contributions) : null;
        const actualTax = backendMonth.cumulative_tax ? parseFloat(backendMonth.cumulative_tax) : null;
        const targetIncome = parseFloat(backendMonth.target_cumulative_income);
        const targetContributions = parseFloat(backendMonth.target_cumulative_contributions);
        const targetTax = parseFloat(backendMonth.target_cumulative_tax);

        // Calculate variance bars (ahead/behind)
        const incomeDiff = actualIncome !== null ? actualIncome - targetIncome : null;
        const contributionsDiff = actualContributions !== null ? actualContributions - targetContributions : null;
        const taxDiff = actualTax !== null ? actualTax - targetTax : null;

        return {
          month: monthName,
          actualPostSacrificeIncome: actualIncome,
          actualContributions: actualContributions,
          actualTax: actualTax,
          expectedIncomeManual: targetIncome,
          targetContributions: targetContributions,
          // Behind bars (negative variance, shown below target)
          incomeBehind: incomeDiff !== null && incomeDiff < 0 ? Math.abs(incomeDiff) : null,
          contributionsBehind: contributionsDiff !== null && contributionsDiff < 0 ? Math.abs(contributionsDiff) : null,
          taxBehind: taxDiff !== null && taxDiff < 0 ? Math.abs(taxDiff) : null,
          // Ahead bars (positive variance, shown above target)
          incomeAhead: incomeDiff !== null && incomeDiff > 0 ? incomeDiff : null,
          contributionsAhead: contributionsDiff !== null && contributionsDiff > 0 ? contributionsDiff : null,
          taxAhead: taxDiff !== null && taxDiff > 0 ? taxDiff : null,
          // Base values for stacking (target value when ahead)
          incomeBase: incomeDiff !== null && incomeDiff > 0 ? targetIncome : null,
          contributionsBase: contributionsDiff !== null && contributionsDiff > 0 ? targetContributions : null,
          taxBase: taxDiff !== null && taxDiff > 0 ? targetTax : null,
        };
      });
    }

    // Fallback to client-side calculation for logged-out users
    const completedMonths = values.completedMonths;

    // Track cumulative actual values
    let cumGrossIncome = 0;
    let cumContributions = 0;
    let cumTax = 0;

    // Create a lookup map for O(1) access instead of O(n) find in loop
    const monthsMap = new Map(months.map(m => [m.month, m]));

    return TAX_YEAR_MONTHS.map((monthName, idx) => {
      const isCompleted = idx < completedMonths;
      const monthData = monthsMap.get(monthName);
      const hasDataThisMonth = monthData !== undefined;

      // Accumulate actuals if data exists
      // For completed months OR current month with data, always accumulate (even if 0)
      // Include current month (idx === completedMonths) if it has data
      const isCurrentMonthWithData = idx === completedMonths && hasDataThisMonth;
      if (isCompleted || isCurrentMonthWithData) {
        if (monthData) {
          cumGrossIncome += monthData.grossSalary;
          cumContributions += (monthData.pension || 0) + (monthData.charity || 0);
          cumTax += (monthData.incomeTax || 0) + (monthData.ni || 0);
        }
        // else: month has no data, but we still count it as completed (adds 0)
      }

      // Determine if we should show actual values
      // Show actuals for all completed months + current month if it has data
      const showActuals = isCompleted || isCurrentMonthWithData;

      // Cap at 12 months so Apr (Y2) uses same target as March
      const monthsForTarget = Math.min(idx + 1, 12);
      const targetContributions = ((values.targetPensionContributions + values.targetCharitableDonations) / 12) * monthsForTarget;
      const expectedIncomeValue = ((values.expectedIncomeManual || values.projectedGrossIncome) / 12) * monthsForTarget;

      // Calculate bar chart values - handle both behind and ahead of target
      let incomeBehind = null;
      let incomeAhead = null;
      let incomeBase = null;
      let contributionsBehind = null;
      let contributionsAhead = null;
      let contributionsBase = null;
      let taxBehind = null;
      let taxAhead = null;
      let taxBase = null;

      if (showActuals) {
        // Income: compare actual vs expected
        if (cumGrossIncome >= expectedIncomeValue) {
          // Ahead of target
          incomeBase = expectedIncomeValue;
          incomeAhead = cumGrossIncome - expectedIncomeValue;
          incomeBehind = null;
        } else {
          // Behind target
          incomeBase = cumGrossIncome;
          incomeAhead = null;
          incomeBehind = expectedIncomeValue - cumGrossIncome;
        }

        // Contributions: compare actual vs target
        if (cumContributions >= targetContributions) {
          // Ahead of target
          contributionsBase = targetContributions;
          contributionsAhead = cumContributions - targetContributions;
          contributionsBehind = null;
        } else {
          // Behind target
          contributionsBase = cumContributions;
          contributionsAhead = null;
          contributionsBehind = targetContributions - cumContributions;
        }

        // Tax: calculate expected tax from strategy's annual tax estimate
        // Cap at 12 months so Apr (Y2) uses same target as March
        const expectedTax = values.expectedAnnualTax > 0
          ? (values.expectedAnnualTax / 12) * monthsForTarget
          : 0;

        if (expectedTax > 0) {
          if (cumTax >= expectedTax) {
            // Ahead of expected (paying more tax - shown as caution)
            taxBase = expectedTax;
            taxAhead = cumTax - expectedTax;
            taxBehind = null;
          } else {
            // Behind expected (paying less tax - shown as good)
            taxBase = cumTax;
            taxAhead = null;
            taxBehind = expectedTax - cumTax;
          }
        }
      }

      return {
        month: monthName,
        // Actual values
        actualPostSacrificeIncome: showActuals ? cumGrossIncome : null,
        actualContributions: showActuals ? cumContributions : null,
        actualTax: showActuals ? cumTax : null,
        // Expected values (always show) - use manual expectation if set, otherwise fall back to projection
        expectedIncomeManual: expectedIncomeValue,
        targetContributions,
        // Bar chart values
        incomeBehind,
        incomeAhead,
        incomeBase,
        contributionsBehind,
        contributionsAhead,
        contributionsBase,
        taxBehind,
        taxAhead,
        taxBase,
      };
    });
  }, [backendChartData, values, months]);

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <h4 className="text-lg font-semibold text-gray-900 mb-4">{CHART_TITLE}</h4>
      <ResponsiveContainer width="100%" height={CHART_CONFIG.height}>
        <ComposedChart
          data={chartData}
          margin={CHART_CONFIG.margin}
        >
          <defs>
            {/* Stripe patterns - behind target (diagonal up-right) */}
            <pattern id="incomeBehind" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke={CHART_COLORS.income} strokeWidth="4" opacity="0.3" />
            </pattern>
            <pattern id="contributionsBehind" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke={CHART_COLORS.contributions} strokeWidth="4" opacity="0.3" />
            </pattern>
            <pattern id="taxBehind" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke={CHART_COLORS.tax} strokeWidth="4" opacity="0.3" />
            </pattern>
            {/* Stripe patterns - ahead of target (diagonal up-left, inverted) */}
            <pattern id="incomeAhead" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(-45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke={CHART_COLORS.income} strokeWidth="4" opacity="0.3" />
            </pattern>
            <pattern id="contributionsAhead" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(-45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke={CHART_COLORS.contributions} strokeWidth="4" opacity="0.3" />
            </pattern>
            <pattern id="taxAhead" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(-45)">
              <line x1="0" y1="0" x2="0" y2="8" stroke={CHART_COLORS.tax} strokeWidth="4" opacity="0.3" />
            </pattern>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="month"
            tick={AXIS_TICK_STYLE}
            axisLine={AXIS_LINE_STYLE}
          />
          <YAxis
            yAxisId="income"
            orientation="left"
            tick={AXIS_TICK_STYLE}
            axisLine={AXIS_LINE_STYLE}
            tickFormatter={formatYAxisTick}
            label={{ value: 'Income / Tax', angle: -90, position: 'insideLeft', style: { fill: '#6b7280', fontSize: 12 } }}
          />
          <YAxis
            yAxisId="contributions"
            orientation="right"
            tick={AXIS_TICK_STYLE}
            axisLine={AXIS_LINE_STYLE}
            tickFormatter={formatYAxisTick}
            label={{ value: 'Contributions', angle: 90, position: 'insideRight', style: { fill: CHART_COLORS.contributions, fontSize: 12 } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="rect"
            formatter={legendFormatter}
          />

          {/* Income: solid actual + striped deficit on top */}
          <Bar
            dataKey="actualPostSacrificeIncome"
            name="Actual Income"
            fill={CHART_COLORS.income}
            stackId="income"
            yAxisId="income"
            isAnimationActive={false}
          />
          <Bar
            dataKey="incomeBehind"
            name="Income Below Target"
            fill="url(#incomeBehind)"
            stackId="income"
            yAxisId="income"
            isAnimationActive={false}
          />

          {/* Contributions: solid actual + striped deficit on top */}
          <Bar
            dataKey="actualContributions"
            name="Actual Contributions"
            fill={CHART_COLORS.contributions}
            stackId="contributions"
            yAxisId="contributions"
            isAnimationActive={false}
          />
          <Bar
            dataKey="contributionsBehind"
            name="Contributions Below Target"
            fill="url(#contributionsBehind)"
            stackId="contributions"
            yAxisId="contributions"
            isAnimationActive={false}
          />

          {/* Tax: solid actual + striped deficit on top */}
          <Bar
            dataKey="actualTax"
            name="Actual Tax"
            fill={CHART_COLORS.tax}
            stackId="tax"
            yAxisId="income"
            isAnimationActive={false}
          />
          <Bar
            dataKey="taxBehind"
            name="Tax Below Expected"
            fill="url(#taxBehind)"
            stackId="tax"
            yAxisId="income"
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
